import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslations } from 'next-intl';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Image from 'next/image';
import { NavItem } from './components';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SailLogo from '/public/icons/saillogo.svg';
import { NAV_CATEGORY } from 'layouts/Main/constants';
import SailLogoIcon from 'assets/icons/saillogo.svg?icon';

interface Props {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSidebarOpen: () => void;
  pages: {
    landings: Array<PageItem>;
    company: Array<PageItem>;
    account: Array<PageItem>;
    secondary: Array<PageItem>;
    blog: Array<PageItem>;
    portfolio: Array<PageItem>;
  };
  colorInvert?: boolean;
  logoColor?: string;
}

const Topbar = ({
  onSidebarOpen,
  pages,
  colorInvert = false,
  logoColor,
}: Props): JSX.Element => {
  const t = useTranslations('Home');
  const theme = useTheme();

  const {
    landings: landingPages,
    secondary: secondaryPages,
    company: companyPages,
    portfolio: portfolioPages,
    account: accountPages,
    // blog: blogPages,
  } = pages;

  const handleLanguage = () => {
    window.location.href = `https://www.sailglobal.com`;
  };

  // 页面顶部导航
  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      width={1}
      my={1}
    >
      <Box sx={{ display: { xs: 'none', md: 'flex' } }} alignItems={'center'}>
        <Box
          display={'flex'}
          component="a"
          href="/"
          title="sailglobal"
          width={{ xs: 100, md: 120 }}
          marginRight={4}
        >
          {logoColor && colorInvert ? (
            <SailLogoIcon color="#fff" fill="currentcolor" />
          ) : (
            <Image src={SailLogo} alt="sailglobal logo" />
          )}
        </Box>
        <Box>
          <NavItem
            title={t('nav.products')}
            id={'landing-pages'}
            items={landingPages}
            colorInvert={colorInvert}
            type={NAV_CATEGORY.Production.value}
          />
        </Box>
        <Box marginLeft={4}>
          <NavItem
            title={t('nav.solutions')}
            id={'company-pages'}
            items={companyPages}
            colorInvert={colorInvert}
            type={NAV_CATEGORY.Solution.value}
          />
        </Box>
        <Box marginLeft={4}>
          <NavItem
            title={t('nav.resources')}
            id={'account-pages'}
            items={accountPages}
            colorInvert={colorInvert}
            type={NAV_CATEGORY.Resource.value}
          />
        </Box>
        <Box marginLeft={4}>
          <NavItem
            title={t('nav.pricing')}
            id={'prices-pages'}
            items={portfolioPages}
            colorInvert={colorInvert}
            showPanel={false}
            type={NAV_CATEGORY.Pricing.value}
          />
        </Box>
        <Box marginLeft={4}>
          <NavItem
            title={t('nav.about')}
            id={'secondary-pages'}
            items={secondaryPages}
            colorInvert={colorInvert}
            type={NAV_CATEGORY.About.value}
          />
        </Box>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' } }} alignItems={'center'}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ mr: 1 }}
        >
          <Button
            onClick={handleLanguage}
            startIcon={<LanguageOutlinedIcon />}
            variant="text"
            sx={{
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <Box sx={{ fontWeight: 900 }}>中</Box>/
            <Box sx={{ fontWeight: 400 }}>En</Box>
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            size="medium"
            rel="noopener noreferrer"
            sx={{ px: 3, py: 0.7, color: '#0644F4', bgcolor: '#fff', mr: 2 }}
            href={'/book-demo'}
            aria-label={'预约演示'}
          >
            预约演示
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            rel="noopener noreferrer"
            sx={{ px: 3, py: 0.7, bgcolor: '#0644F4' }}
            onClick={() => {
              window.open('https://app.sailglobal.com/en/login', '_blank');
            }}
          >
            {t('login')}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{ display: { xs: 'flex', md: 'none' } }}
        alignItems={'center'}
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display={'flex'}
          component="a"
          href="/"
          title="sailglobal"
          width={{ xs: 100, md: 120 }}
          marginRight={4}
          alignItems={'center'}
        >
          {logoColor && colorInvert ? (
            <SailLogoIcon color="#fff" fill="currentcolor" />
          ) : (
            <Image src={SailLogo} alt="sailglobal logo" />
          )}
        </Box>
        <Box>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ mr: 1 }}
            >
              <Button
                onClick={handleLanguage}
                startIcon={<LanguageOutlinedIcon />}
                variant="text"
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Box sx={{ fontWeight: 900 }}>中</Box>/
                <Box sx={{ fontWeight: 400 }}>En</Box>
              </Button>
            </Box>
            <Button
              onClick={() => onSidebarOpen()}
              aria-label="Menu"
              variant={'outlined'}
              sx={{
                borderRadius: 2,
                minWidth: 'auto',
                padding: 1,
                borderColor: alpha(theme.palette.divider, 0.2),
              }}
            >
              <MenuIcon />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Topbar;
